/**
 * Phase 45 Plan 45-06 — sticky project group header.
 *
 * Per UI-SPEC §Project group header (lines 383-386):
 *   sticky top-0 z-10 backdrop-blur bg-ms-surface-2/95 border-y
 *
 * Petra UAT 2026-05-19 — collapse chevron restored. The chevron points
 * down when expanded, right when collapsed. Click toggles. Persistence
 * lives one level up in MyTasksProjectGroup (sessionStorage).
 */
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export interface MyTasksProjectHeaderProps {
  projectId: number;
  projectName: string;
  taskCount: number;
  customerColorHex?: string | null;
  isSla?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

export function MyTasksProjectHeader({
  projectId,
  projectName,
  taskCount,
  customerColorHex: _customerColorHex,
  isSla = false,
  collapsed = false,
  onToggleCollapsed,
}: MyTasksProjectHeaderProps) {
  const Chevron = collapsed ? ChevronRightIcon : ChevronDownIcon;
  return (
    <div
      data-testid="my-tasks-project-header"
      className="sticky top-0 z-10 flex items-center gap-2.5 px-4 py-1.5 bg-ms-surface-2/95 backdrop-blur border-y border-ms-border-subtle"
    >
      {onToggleCollapsed && (
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Expand ${projectName}` : `Collapse ${projectName}`}
          data-testid="my-tasks-project-header-toggle"
          className="flex items-center justify-center w-4 h-4 rounded hover:bg-ms-surface-2 text-ms-text-muted hover:text-ms-text-strong focus:outline-none focus:ring-2 focus:ring-ms-orange/40"
        >
          <Chevron className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
      {/* Petra UAT 2026-05-19 — customer color dot dropped per request:
          chevron + project name carry enough identity without it. The
          customerColorHex prop is kept on the API so callers don't break
          (and so we can flip it back on by uncommenting one span). */}
      <span className="text-[12px] font-semibold text-ms-text-strong">{projectName}</span>
      <span className="text-[10.5px] text-ms-text-muted tabular-nums">{taskCount}</span>
      {isSla && (
        <span
          data-testid="my-tasks-project-header-sla"
          className="text-[9px] font-bold uppercase tracking-wider text-rose-600 px-1 py-0.5 border border-rose-200 bg-rose-50 rounded"
        >
          SLA
        </span>
      )}
      <Link
        to={`/projects/${projectId}/board`}
        className="ml-auto text-[10.5px] font-medium text-ms-text-muted hover:text-ms-text-strong"
        onClick={(e) => e.stopPropagation()}
      >
        Open board →
      </Link>
    </div>
  );
}
